import Handlebars from "handlebars";
import { registerSharedHandlebarHelpers } from "@dreambigger/shared/src/utils/handlebarHelpers";

registerSharedHandlebarHelpers(Handlebars);

/** Hard cap on the number of distinct compiled templates retained in memory.
 * In practice the number of distinct templates per flow is small and finite,
 * so this cap is defensive against misconfigured or dynamically-rewritten
 * templates. `Map` preserves insertion order; eviction is FIFO, which is a
 * reasonable approximation of LRU for a cache this small. */
const TEMPLATE_CACHE_MAX = 256;

type Compiled = ReturnType<typeof Handlebars.compile>;
const templateCache = new Map<string, Compiled>();

function getCompiled(template: string): Compiled {
  const existing = templateCache.get(template);
  if (existing) {
    // Refresh recency — delete + re-insert moves this entry to the end of the
    // Map's insertion order so the oldest entry is the next eviction target.
    templateCache.delete(template);
    templateCache.set(template, existing);
    return existing;
  }
  const compiled = Handlebars.compile(template);
  templateCache.set(template, compiled);
  if (templateCache.size > TEMPLATE_CACHE_MAX) {
    // Evict the oldest entry (first key in insertion order).
    const oldest = templateCache.keys().next().value;
    if (oldest !== undefined) templateCache.delete(oldest);
  }
  return compiled;
}

/** Compile a Handlebars template against an arbitrary data object. Returns
 *  the rendered string, or "" on error. */
export default function compileTemplate(
  template: string,
  data: Record<string, unknown>
): string {
  try {
    return getCompiled(template)(data).trim();
  } catch {
    return "";
  }
}
